import { AuthErrorType } from '@/actors/AuthActor/types'

/**
 * Client-side codes for federated (Google/Apple) sign-in failures. They are
 * strings on purpose: `AuthErrorModal` keys its copy by `error.code`, and a
 * numeric code there belongs to the backend — minting one would collide the
 * day fx-npm-definitions assigns it. `NO_TOKEN` is the existing precedent.
 */
export const SSO_ERROR_CODES = {
  /** The federated identity exists, but no account here is linked to it. */
  ACCOUNT_NOT_FOUND: 'SSO_ACCOUNT_NOT_FOUND',
  /** Anything else that is not a popup the visitor dismissed. */
  FAILED: 'SSO_FAILED',
} as const

/**
 * `validations/userNotRegister` as fx-npm-definitions resolves it. On the
 * federated path this is the *only* producer: `providerBasedLogin/firebase`
 * answers it when the decoded identity has no `User` and the request carried
 * `denyRegister` (the sign-in-only scenes — /firetv and friends).
 */
const USER_NOT_REGISTERED_CODE = 42215

/**
 * Heading for anything whose own error carries none. The modal's own fallback
 * is 'Something went wrong', which says nothing about where the visitor is —
 * the body still comes from the code's own entry.
 */
const GENERIC_SSO_HEADING = 'We could not complete your sign-in'

/**
 * Turn a federated sign-in rejection into the envelope `AuthErrorModal` reads.
 *
 * Two rejections arrive here and they look nothing alike: the Firebase SDK
 * throws a `FirebaseError` (`code: 'auth/…'`, no `userMessage`), while the
 * backend rejection comes through the BFF as `{ code, userMessage, extra }`.
 * Mapping both to one shape is what lets a single `<AuthErrorModal>` cover the
 * path — which, until PRO-736, rendered nothing at all for either.
 *
 * 42215 does not pass through: the copy already keyed to it is written for the
 * email login ("The email address you entered is not associated with an
 * account", offering Register). The visitor here entered no email, and on the
 * scene that produces this error registering is exactly what was denied — so
 * it gets its own entry pointing at the one route that can work, email login.
 */
export const toSsoAuthError = (error: unknown): AuthErrorType => {
  const backendError = typeof error === 'object' && error !== null
    ? (error as { code?: string | number; userMessage?: AuthErrorType['userMessage']; extra?: unknown })
    : undefined

  const extra = (backendError?.extra ?? {}) as AuthErrorType['extra']

  if (Number(backendError?.code) === USER_NOT_REGISTERED_CODE) {
    return {
      code: SSO_ERROR_CODES.ACCOUNT_NOT_FOUND,
      userMessage: 'There is no account linked to this sign-in',
      extra,
    }
  }

  // A backend error with its own code keeps it: 5001, 429 and the rest already
  // have copy in AuthErrorModal, and their `userMessage` is localized upstream.
  // `auth/…` codes are the SDK's, not the backend's, and fall through.
  const backendCode = backendError?.code
  const hasBackendCode = typeof backendCode === 'number'
    || (typeof backendCode === 'string' && !backendCode.startsWith('auth/'))

  if (hasBackendCode) {
    return {
      code: backendCode as string | number,
      userMessage: backendError?.userMessage ?? GENERIC_SSO_HEADING,
      extra,
    }
  }

  return {
    code: SSO_ERROR_CODES.FAILED,
    userMessage: GENERIC_SSO_HEADING,
    extra,
  }
}
